import { clsx } from 'clsx';
import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
export type FilterOption = { value: string; label: string; is_active?: boolean };

export interface FilterDropdownProps {
  label: string;
  options: FilterOption[];
  value: string[] | string | undefined;
  onChange: (value: string[] | string | undefined) => void;
  multiple?: boolean;
  className?: string;
  pillClassName?: string;
  pillTextClassName?: string;
  menuClassName?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  labelOverride?: string;
}

/** The filter bar's pill and its popover panel, shared by every kind of filter pill. */
export const FILTER_PILL_CLASSES =
  'tw:flex tw:items-center tw:gap-2 tw:!rounded-lg tw:border tw:border-zinc-300 tw:bg-white tw:px-3 tw:py-1.5 tw:text-sm tw:!text-zinc-700 tw:hover:bg-zinc-50 disabled:tw:opacity-60';
export const FILTER_PANEL_CLASSES =
  'tw:absolute tw:z-50 tw:mt-2 tw:min-w-[240px] tw:max-w-[90vw] tw:rounded-xl tw:border tw:border-zinc-200 tw:bg-white tw:shadow-xl tw:focus:outline-none';
/** The small "x" inside a pill that clears its filter. */
export const FILTER_PILL_CLEAR_CLASSES =
  'tw:ml-1 tw:text-zinc-400 tw:hover:text-zinc-700 tw:bg-transparent tw:border-0 tw:p-0 tw:focus:outline-none';

/**
 * Headless pill-style dropdown used by ListFilter.
 * - Multi or single select
 * - Optional client-side search inside the panel
 * - Clear action
 */
// Minimum number of options required to enable the inline search box
const SEARCHABLE_COUNT_THRESHOLD = 15;

export function FilterDropdown({
  label,
  options,
  value,
  onChange,
  multiple = true,
  className,
  pillClassName,
  pillTextClassName,
  menuClassName,
  icon,
  disabled = false,
  labelOverride = undefined,
}: FilterDropdownProps) {
  const buttonId = useId();
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const selectedSet = useMemo(() => {
    if (Array.isArray(value)) return new Set(value);
    if (typeof value === 'string') return new Set([value]);
    return new Set<string>();
  }, [value]);

  const filtered = useMemo(() => {
    const base = options.filter((o) => o.is_active !== false);
    if (!q) return base;
    const norm = q.toLowerCase();
    return base.filter((o) => o.label.toLowerCase().includes(norm));
  }, [options, q]);

  const isSearchable = useMemo(() => {
    const activeCount = options.filter((o) => o.is_active !== false).length;
    return activeCount > SEARCHABLE_COUNT_THRESHOLD;
  }, [options]);

  const selectedCount = selectedSet.size;
  const firstLabel = useMemo(() => {
    if (selectedCount === 0) return undefined;
    const first = options.find((o) => selectedSet.has(o.value));
    return first?.label;
  }, [options, selectedSet, selectedCount]);

  const pillText =
    selectedCount === 0 ? label : selectedCount === 1 ? (firstLabel ?? label) : label;

  function toggle(valueToToggle: string) {
    if (multiple) {
      const next = new Set(selectedSet);
      next.has(valueToToggle) ? next.delete(valueToToggle) : next.add(valueToToggle);
      onChange(Array.from(next));
    } else {
      const current = typeof value === 'string' ? value : undefined;
      onChange(current === valueToToggle ? undefined : valueToToggle);
      // In single-select, close immediately for fast UX
      setOpen(false);
    }
  }

  function clear() {
    onChange(multiple ? [] : undefined);
  }

  // Close on an outside click
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      if (!open) return;
      if (!popoverRef.current) return;
      const target = e.target as Node;
      if (!popoverRef.current.contains(target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <div className={clsx('tw:relative', className)} ref={popoverRef}>
      <button
        id={buttonId}
        type="button"
        disabled={disabled}
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((o) => !o)}
        className={twMerge(clsx(FILTER_PILL_CLASSES, pillTextClassName, pillClassName))}
      >
        {icon}
        <span className="tw:truncate">{pillText}</span>
        {selectedCount > 1 && (
          <span className="tw:ml-1 tw:text-[11px] tw:px-1.5 tw:py-0.5 tw:bg-brand-green-2 tw:font-medium tw:text-zinc-700 tw:rounded">
            {selectedCount}
          </span>
        )}
        <i className={twMerge(clsx('fas fa-chevron-down tw:text-zinc-500', pillTextClassName))} />
        {selectedCount > 0 && (
          <button
            type="button"
            aria-label={`Clear ${label}`}
            onClick={(e) => {
              e.stopPropagation();
              clear();
            }}
            className={FILTER_PILL_CLEAR_CLASSES}
          >
            <i className={twMerge(clsx('fas fa-xmark', pillTextClassName))} />
          </button>
        )}
      </button>

      {open && (
        <div
          id={panelId}
          role="dialog"
          aria-labelledby={buttonId}
          className={clsx(FILTER_PANEL_CLASSES, menuClassName)}
        >
          <div className="tw:flex tw:items-center tw:justify-between tw:px-3 tw:py-2 tw:border-b tw:border-zinc-100">
            <div className="tw:text-sm tw:font-medium tw:text-brand-green-0">
              {labelOverride ? labelOverride : `Filter by ${label}`}
            </div>
            <button
              type="button"
              className="tw:text-xs tw:text-zinc-500 tw:hover:text-brand-green-1"
              onClick={clear}
            >
              Clear
            </button>
            <button
              type="button"
              className="tw:text-xs tw:text-zinc-500 tw:hover:text-brand-green-1"
              onClick={() => setOpen((o) => !o)}
            >
              <i className="fas fa-times" />
            </button>
          </div>

          {isSearchable && (
            <div className="tw:p-2">
              <input
                className="tw:w-full tw:rounded tw:border tw:border-zinc-300 tw:px-2 tw:py-1 tw:text-sm tw:text-zinc-700 placeholder:tw:text-zinc-400 focus:tw:outline-none focus:tw:ring-2 focus:tw:ring-brand-green-1"
                placeholder={`Search ${label}...`}
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
            </div>
          )}

          <ul className="tw:max-h-[50vh] tw:overflow-auto tw:!pl-2">
            {filtered.map((opt) => {
              const isSelected = selectedSet.has(opt.value);
              return (
                <li key={opt.value} className="">
                  <button
                    type="button"
                    className={clsx(
                      'tw:flex tw:w-full tw:items-center tw:gap-2 tw:px-1 tw:py-2 tw:text-left tw:hover:bg-brand-green-3',
                      isSelected && 'tw:bg-zinc-50'
                    )}
                    onClick={() => toggle(opt.value)}
                  >
                    {multiple ? (
                      <input
                        type="checkbox"
                        readOnly
                        checked={isSelected}
                        className="tw:accent-brand-green-1"
                      />
                    ) : (
                      <span
                        className={clsx(
                          'tw:inline-block tw:h-4 tw:w-4 tw:shrink-0 tw:rounded-full tw:border tw:aspect-square',
                          isSelected
                            ? 'tw:bg-brand-green-1 tw:border-brand-green-1'
                            : 'tw:border-zinc-300'
                        )}
                        aria-hidden
                      />
                    )}
                    <span className="tw:text-sm tw:text-zinc-800">{opt.label}</span>
                  </button>
                </li>
              );
            })}
            {filtered.length === 0 && (
              <li className="tw:px-3 tw:py-2 tw:text-sm tw:text-zinc-500">No results</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default FilterDropdown;
